import { useState, useEffect } from "react";
import PopAnimation from "./PopAnimation";
import { useUI, useUser } from "../hooks/index.js";

const IntroContent = ({ loggedIn }) => {
  const { isMobile } = useUI();
  const { showIntroFlag, setShowIntroFlag } = useUser();
  const baseURL =
    process.env.NODE_ENV === "production"
      ? "https://www.prioritypop.com"
      : "http://localhost:3001";
  const [page, setPage] = useState(1); // Track the current page
  const [displayContent, setDisplayContent] = useState(false); // Control content display
  const [inOverlay, setInOverlay] = useState(true);

  useEffect(() => {
    if (page === 1) {
      const timer = setTimeout(() => setDisplayContent(true), 1200); // Delay content display
      return () => clearTimeout(timer); // Clean up timer when unmounting or changing pages
    } else {
      setDisplayContent(true); // Immediately display content on Page 2+
    }
  }, [page]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 20px",
        //justifyContent: 'center',
        height: "100%",
        textAlign: "left",
      }}
    >
      {page === 1 && (
        <>
          {/* Page 1 Header */}
          {!displayContent && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%", // Full height to center vertically
                textAlign: "center", // Center text horizontally
              }}
            >
              <h2 className="overlay-header">Get to Know PriorityPop</h2>
              <p> </p>
              <PopAnimation loggedIn={loggedIn} inOverlay={inOverlay} />
            </div>
          )}

          {/* Page 1 Content */}
          {displayContent && (
            <>
              <h3 style={{ margin: "0.4rem 0 1.2rem 0", textAlign: "center" }}>
                Bubbles Provide 'At-a-Glance' Information
              </h3>

              {/* Bubble size */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/media/bubble_default.png`}
                    alt="Large Bubble"
                    style={{ width: "50px", height: "50px" }}
                  />
                </div>
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/media/bubble_default.png`}
                    alt="Small Bubble"
                    style={{ width: "20px", height: "20px" }}
                  />
                </div>
                <p style={{ flex: "1" }}>
                  Bubble size visualizes the effort required for the task.
                </p>
              </div>

              {/* Bubble opacity */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/media/bubble_blue.png`}
                    alt="Opaque Bubble"
                    style={{ width: "40px", height: "40px", opacity: 1 }}
                  />
                </div>
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/media/bubble_blue.png`}
                    alt="Transparent Bubble"
                    style={{ width: "40px", height: "40px", opacity: 0.25 }}
                  />
                </div>
                <p style={{ flex: "1" }}>
                  Opaque bubbles show tasks with more impact.
                </p>
              </div>

              {/* Bubble color */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: "0 0 auto", display: "flex", gap: "10px" }}>
                  {[
                    "bubble_pink.png",
                    "bubble_purple.png",
                    "bubble_red.png",
                  ].map((file, index) => (
                    <img
                      key={index}
                      src={`${process.env.PUBLIC_URL}/media/${file}`}
                      alt="Colored Bubble"
                      style={{ width: "40px", height: "40px" }}
                    />
                  ))}
                </div>
                <p style={{ flex: "1" }}>
                  Set different colors to sort categories.
                </p>
              </div>

              {/* Moving bubble */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    className="shaking-bubble"
                    src={`${process.env.PUBLIC_URL}/media/bubble_orange.png`}
                    alt="Shaking Bubble"
                    style={{ width: "40px", height: "40px" }}
                  />
                </div>
                <p style={{ flex: "1" }}>
                  Moving bubbles draw your attention to the nearest or overdue
                  deadlines.
                </p>
              </div>
              {/* Stuck bubble */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: "0 0 auto" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/media/stuck.png`}
                    alt="Shaking Bubble"
                    style={{ width: "30px", height: "30px" }}
                  />
                </div>
                <p style={{ flex: "1" }}>
                  A small stuck icon over a bubble shows tasks that require more
                  help.
                </p>
              </div>

              {/* Next Button */}
              <button
                onClick={() => {
                  setPage(2);
                  setDisplayContent(false); // Reset displayContent for next page
                }}
                style={{
                  margin: "auto",
                  padding: "10px 20px",
                  backgroundColor: "var(--purple-light)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Next
              </button>
              {loggedIn && (
                <div style={{ marginTop: "1rem", textAlign: "center" }}>
                  <label style={{ fontSize: "0.9rem" }}>
                    <input
                      type="checkbox"
                      checked={!showIntroFlag}
                      onChange={(e) => {
                        const newValue = !e.target.checked;
                        setShowIntroFlag(newValue);
                        fetch(`${baseURL}/setShowIntro`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ showIntroFlag: newValue }),
                        });
                      }}
                      style={{ marginRight: "8px" }}
                    />
                    Don't show tips on startup
                  </label>
                </div>
              )}
            </>
          )}
        </>
      )}

      {page === 2 && (
        <>
          {/* Page 2 Content */}
          <h3 style={{ margin: "0.4rem 0 1.4rem 0", textAlign: "center" }}>
            Interacting with Bubbles
          </h3>
          <ul style={{ textAlign: "left", lineHeight: "2.0" }}>
            {isMobile ? (
              ""
            ) : (
              <li>Hover cursor over a bubble to see the deadline.</li>
            )}
            <li>
              {isMobile
                ? "Long-press a bubble to open its edit menu."
                : "Right-click a bubble to open its edit menu."}
            </li>
            <li>
              {isMobile
                ? "Drag a bubble to the timer to start tracking time on task."
                : "Click and drag a bubble to the timer to start tracking time on task."}
            </li>
            <li>
              {isMobile
                ? "Double tap the timer to stop working on the task."
                : "Double-click the timer to stop working on the task."}
            </li>
            <li>
              {isMobile
                ? "Double tap a bubble to mark it complete."
                : "Double-click a bubble to mark it complete."}
            </li>
          </ul>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginTop: "1rem",
            }}
          >
            {/* Previous Button */}
            <button
              onClick={() => {
                setPage(1);
                setDisplayContent(false); // Reset displayContent for previous page
              }}
              style={{
                margin: "auto",
                padding: "10px 20px",
                backgroundColor: "var(--purple-light)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Previous
            </button>
            {/* Next Button */}
            {loggedIn && (
              <button
                onClick={() => {
                  setPage(3);
                  setDisplayContent(false); // Reset displayContent for next page
                }}
                style={{
                  margin: "auto",
                  padding: "10px 20px",
                  backgroundColor: "var(--purple-light)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Next
              </button>
            )}
          </div>
          {loggedIn && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <label style={{ fontSize: "0.9rem" }}>
                <input
                  type="checkbox"
                  checked={!showIntroFlag}
                  onChange={(e) => {
                    const newValue = !e.target.checked;
                    setShowIntroFlag(newValue);
                    fetch(`${baseURL}/setShowIntro`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ showIntroFlag: newValue }),
                    });
                  }}
                  style={{ marginRight: "8px" }}
                />
                Don't show tips on startup
              </label>
            </div>
          )}
        </>
      )}
      {page === 3 && (
        <>
          {/* Page 3 Content */}
          {page === 3 && (
            <div
              style={{
                //maxWidth: "520px",
                margin: "0 auto",
                textAlign: "left",
              }}
            >
              <h3
                style={{
                  marginBottom: "12px",
                  textAlign: "center",
                  fontSize: "1.2rem",
                }}
              >
                Fine-Tuning Your Workspace
              </h3>

              <p style={{ fontSize: "1rem", marginBottom: "10px" }}>
                <strong>Task Slates:</strong>
                <br></br>Organize tasks by context, like <em>Work</em>,{" "}
                <em>Home</em>, or <em>Other</em>. Free users get 1 slate; paid
                users can use up to 3.
              </p>

              <p style={{ fontSize: "1rem", marginBottom: "10px" }}>
                <strong>Preferences (paid subscribers only):</strong>
                <br></br>Open the <em>Side Menu</em> → <em>Preferences</em> to:
                <br />• Create color-coded categories on each slate
                <br />• Choose a custom font for task bubble labels
              </p>

              <p style={{ fontSize: "1rem", marginBottom: "10px" }}>
                <strong>Effort, Impact & Deadlines:</strong>
                <br></br>When adding a task, you can optionally select:
                <br />• Effort - how demanding it is. Select from Low (L),
                Medium (M), High (H), or Taxing (T)
                <br />• Impact - how valuable the result is. Select from Low
                (L), Moderate (M), High (H), or Transformative (T)
                <br />• Deadline - to set a due date for the task which will
                prioritize it over tasks with matching effort and impact
                <br />• If left blank, a hidden “default” level (between M and
                H) is applied to keep your slate optimized.
              </p>

              <p style={{ fontSize: "0.85rem", marginTop: "16px" }}>
                To refresh your layout at any time and re-optimize task
                positions, just click the <strong>PriorityPop</strong> logo in
                the top left.
              </p>
            </div>
          )}
          {/* Previous Button */}
          <button
            onClick={() => {
              setPage(2);
              setDisplayContent(false); // Reset displayContent for previous page
            }}
            style={{
              margin: "auto",
              padding: "10px 20px",
              backgroundColor: "var(--purple-light)",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          {loggedIn && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <label style={{ fontSize: "0.9rem" }}>
                <input
                  type="checkbox"
                  checked={!showIntroFlag}
                  onChange={(e) => {
                    const newValue = !e.target.checked;
                    setShowIntroFlag(newValue);
                    fetch(`${baseURL}/setShowIntro`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ showIntroFlag: newValue }),
                    });
                  }}
                  style={{ marginRight: "8px" }}
                />
                Don't show tips on startup
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntroContent;
